import type { User, UserBase } from "../../types";
import type { IOperationResult } from "../types";
import { getSupabase as getSupabaseNullable } from "../../lib/supabase";
import { createBaseService, fromDb } from "./baseService";

function requireSupabase() {
  const client = getSupabaseNullable();
  if (!client) throw new Error("Supabase not initialized. Call initSupabase() first.");
  return client;
}

const base = createBaseService<User, UserBase>({
  tableName: "profiles",
  viewName: "profiles_enriched",
  searchColumns: ["email", "first_name", "last_name"],
  computedColumns: ["teamName"],
  skipAuditFields: true,
});

export class UserService {
  static getAll = base.getAll;
  static get = base.get;

  /**
   * Create a user via the create-user Edge Function.
   * Direct insert into profiles fails because of FK to auth.users.
   * The Edge Function creates the auth user first, then configures the profile.
   */
  static async create(record: UserBase): Promise<IOperationResult<User>> {
    try {
      const supabase = requireSupabase();

      const { data, error } = await supabase.functions.invoke("create-user", {
        body: {
          email: record.email,
          firstName: record.firstName,
          lastName: record.lastName,
          role: record.role,
          licenseType: record.licenseType,
          teamId: record.teamId,
          status: record.status,
        },
      });

      if (error) {
        return { success: false, error: error.message ?? "Failed to create user" };
      }

      // Edge Function returns { success, data, error }
      if (!data?.success) {
        return { success: false, error: data?.error ?? "Failed to create user" };
      }

      return {
        success: true,
        data: fromDb<User>(data.data as Record<string, unknown>),
      };
    } catch (error) {
      return { success: false, error };
    }
  }

  static update = base.update;
  static delete = base.delete;
}
